/**
 * @fileoverview Authentication Redux slice.
 * @summary Manages authentication state (user, tokens, login status).
 *
 * @package KaraoNow
 * @module apps/front/src/store/slices/authSlice
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'
import i18n, { type SupportedLanguage } from '../../utils/i18n/index.js'

/**
 * @summary User interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface User {
  userId: string
  email: string
  role: 'superadmin' | 'organizer' | 'singer'
  profile: {
    firstName?: string
    lastName?: string
    pseudo?: string
    organization?: string
    avatar?: string
    preferredLanguage?: SupportedLanguage
  }
  pendingEmail?: string
  subscription?: {
    type: 'free' | 'single_event' | 'monthly_subscription'
    status?: 'active' | 'canceled' | 'expired'
    subscriptionStartDate?: string | Date // Start of current billing period
    subscriptionPeriodEnd?: string | Date // End of current billing period (from Stripe current_period_end)
    subscriptionExpiresAt?: string | Date // Only set when subscription is canceled (cancel_at_period_end)
  }
}

/**
 * @summary Authentication state interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  hasCheckedAuth: boolean // Track if we've already checked authentication on mount
}

/**
 * @summary Initial authentication state.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to check authentication on mount
  error: null,
  hasCheckedAuth: false, // Track if we've already checked authentication on mount
}

/**
 * @summary Register a new user.
 * @summary Language is automatically detected from Accept-Language header via apiClient.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const register = createAsyncThunk(
  'auth/register',
  async (credentials: { email: string; password: string; role: 'organizer' | 'singer'; firstName?: string; pseudo?: string }) => {
    const response = await apiRequest<{ user: User }>({
      method: 'POST',
      url: API_ENDPOINTS.AUTH.REGISTER,
      data: credentials,
    })

    if (!response.success) {
      throw new Error(response.message || 'Registration failed')
    }

    return response.data!.user
  },
)

/**
 * @summary Login user.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }) => {
    const response = await apiRequest<{ user: User }>({
      method: 'POST',
      url: API_ENDPOINTS.AUTH.LOGIN,
      data: credentials,
    })

    if (!response.success) {
      throw new Error(response.message || 'Login failed')
    }

    return response.data!.user
  },
)

/**
 * @summary Get current user.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const getCurrentUser = createAsyncThunk('auth/getCurrentUser', async () => {
  const response = await apiRequest<{ user: User }>({
    method: 'GET',
    url: API_ENDPOINTS.AUTH.ME,
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to get current user')
  }

  return response.data!.user
})

/**
 * @summary Logout user.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const logout = createAsyncThunk('auth/logout', async () => {
  await apiRequest({
    method: 'POST',
    url: API_ENDPOINTS.AUTH.LOGOUT,
  })
})

/**
 * @summary Update user preferences (language, profile fields, email).
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const updatePreferences = createAsyncThunk(
  'auth/updatePreferences',
  async (preferences: {
    email?: string
    profile?: {
      preferredLanguage?: User['profile']['preferredLanguage']
      firstName?: string
      lastName?: string
      pseudo?: string
      organization?: string
    }
  }) => {
    const response = await apiRequest<{
      preferences: {
        email?: string
        preferredLanguage?: User['profile']['preferredLanguage']
        firstName?: string
        lastName?: string
        pseudo?: string
        organization?: string
        pendingEmail?: string
      }
    }>({
      method: 'PUT',
      url: API_ENDPOINTS.USERS.PREFERENCES,
      data: preferences,
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to update preferences')
    }

    return response.data?.preferences
  },
)

/**
 * @summary Authentication slice.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload
      state.isAuthenticated = !!action.payload
    },
  },
  extraReducers: (builder) => {
    // Register
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
        const pref = action.payload.profile?.preferredLanguage
        if (pref) {
          i18n.changeLanguage(pref)
          localStorage.setItem('i18nextLng', pref)
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Registration failed'
      })

    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
        const pref = action.payload.profile?.preferredLanguage
        if (pref) {
          i18n.changeLanguage(pref)
          localStorage.setItem('i18nextLng', pref)
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Login failed'
      })

    // Get current user
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.hasCheckedAuth = true // Mark as checked
        const pref = action.payload.profile?.preferredLanguage
        if (pref) {
          i18n.changeLanguage(pref)
          localStorage.setItem('i18nextLng', pref)
        }
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.hasCheckedAuth = true // Mark as checked even if it failed
        // Don't set error for getCurrentUser - it's normal to not be authenticated
        // This prevents showing "Access token required" error on login page
      })

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.error = null
        state.hasCheckedAuth = true // Keep hasCheckedAuth true after logout to prevent re-check
        localStorage.removeItem('accessToken')
      })

    // Update preferences (e.g., preferred language)
    builder
      .addCase(updatePreferences.fulfilled, (state, action) => {
        if (state.user && action.payload) {
          const { preferredLanguage, firstName, lastName, pseudo, pendingEmail } = action.payload
          if (preferredLanguage) {
            state.user.profile.preferredLanguage = preferredLanguage
            i18n.changeLanguage(preferredLanguage)
            localStorage.setItem('i18nextLng', preferredLanguage)
          }
          if (firstName !== undefined) {
            state.user.profile.firstName = firstName
          }
          if (lastName !== undefined) {
            state.user.profile.lastName = lastName
          }
          if (pseudo !== undefined) {
            state.user.profile.pseudo = pseudo
          }
          if (pendingEmail !== undefined) {
            state.user.pendingEmail = pendingEmail
          }
        }
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update preferences'
      })
  },
})

export const { clearError, setUser } = authSlice.actions
export default authSlice.reducer
